import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ScorecardView from '../../components/rounds/ScorecardView';
import { roundsAPI, scoresAPI } from '../../services/api';

export default function FriDavePlayerDetail() {
  const { id: roundId, playerId } = useParams();
  const navigate = useNavigate();

  const [round, setRound] = useState(null);
  const [playerEntry, setPlayerEntry] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [position, setPosition] = useState(null);
  const [handicap, setHandicap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [roundId, playerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [fullRound, leaderboard] = await Promise.all([
        roundsAPI.getById(roundId),
        roundsAPI.getLeaderboard(roundId),
      ]);

      setRound(fullRound);

      // Find player in leaderboard for position + points
      const lb = Array.isArray(leaderboard) ? leaderboard : [];
      const lbEntry = lb.find(p => p.player_id === parseInt(playerId));
      if (lbEntry) {
        const pos = lb.findIndex(p => p.player_id === parseInt(playerId)) + 1;
        setPosition(pos);
        setPlayerEntry(lbEntry);
      }

      // Find round_player_id from round.players
      const players = fullRound.players || [];
      const roundPlayer = players.find(p => p.player_id === parseInt(playerId));
      if (roundPlayer) {
        setHandicap(roundPlayer.handicap_at_time);
        const sc = await scoresAPI.getScorecard(roundPlayer.round_player_id);
        setScorecard(sc);
      }
    } catch (err) {
      setError('Failed to load player data');
    } finally {
      setLoading(false);
    }
  };

  const getPosSuffix = (pos) => {
    if (!pos) return '';
    const t = pos % 100, l = pos % 10;
    if (t >= 11 && t <= 13) return 'th';
    if (l === 1) return 'st';
    if (l === 2) return 'nd';
    if (l === 3) return 'rd';
    return 'th';
  };

  const isLive = round && (round.status === 'in_progress' || round.status === 'active');

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  if (error || !round || !playerEntry) {
    return (
      <div className="px-4 pb-4">
        <button onClick={() => navigate(-1)} className="text-fd-primary font-semibold flex items-center gap-1 mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
          <p className="text-gray-500">{error || 'Player not found in this round'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="text-fd-primary font-semibold flex items-center gap-1 mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Player card */}
      <div className="bg-white rounded-2xl p-5 mb-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isLive ? 'bg-green-100 text-green-700 animate-pulse' : 'bg-gray-100 text-gray-500'}`}>
            {isLive ? 'Round in Progress' : 'Round Completed'}
          </span>
          <span className="text-gray-400 text-xs">{round.event_name}</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">
          {playerEntry.player_name}{handicap !== null && <span className="text-lg font-normal text-gray-400 ml-1">({handicap})</span>}
        </h2>
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-bold text-gray-900">{playerEntry.total_points}</span>
          <span className="text-gray-400 text-sm">pts</span>
          {position && (
            <span className="text-gray-600 font-bold text-lg ml-auto">
              {position}{getPosSuffix(position)}
            </span>
          )}
        </div>
        <p className="text-gray-400 text-xs mt-1">{playerEntry.holes_completed} of 18 holes</p>
      </div>

      {/* Scorecard */}
      {scorecard ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <ScorecardView scorecard={scorecard} round={round} hideHeader />
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-6 text-center border border-gray-200">
          <p className="text-gray-400 text-sm">No scorecard available yet</p>
        </div>
      )}
    </div>
  );
}
